import { fetchLibrary } from '../../api/libraryPlayback';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RenderListItem } from '../../api/rendersApi';
import { toApiError } from '../../api/http';
import { EmptyState, StateMessage, PillButton } from '../../components/ui';
import BedtimePairChip from '../../components/BedtimeContext/BedtimePairChip';
import StoryArtwork from '../../components/StoryArtwork/StoryArtwork';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { useApp } from '../../context/AppContext';
import { buildStoryModeSession, writeStoryModeSession } from './storyModeSession';
import { getKnownCacheEpoch } from '../../api/cacheEpochApi';
import { isOfflineCacheCurrent, offlineCacheEntriesForPair } from '../../api/offlineCache';
import { latestApprovedPerStory } from '../Renders/renderPresentation';
import { queuedDurationMinutes } from './storyQueue';
import './StoryTimeSetup.css';

const StoryTimeSetup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, fetchStories } = useApp();
  const context = useBedtimeContext();
  const [approved, setApproved] = useState<RenderListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [queueMode, setQueueMode] = useState(false);
  const [showStoryPicker, setShowStoryPicker] = useState(false);
  const [status, setStatus] = useState<'loading' | 'success' | 'empty' | 'error'>('loading');
  const [error, setError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [starting, setStarting] = useState(false);
  const [networkOnline, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  const online = networkOnline && !state.offlineSession;
  const [loadedPairKey, setLoadedPairKey] = useState<string | null>(null);
  const [offlineBlocked, setOfflineBlocked] = useState(false);
  const handoffControllerRef = React.useRef<AbortController | null>(null);
  const pair = context.pair;
  const userId = state.user?.id || '';
  const currentPairKey = pair ? `${pair.householdId}:${pair.listenerId}:${pair.voiceId}` : '';
  const loadRequestRef = React.useRef(0);
  const pairKeyRef = React.useRef(currentPairKey);
  pairKeyRef.current = currentPairKey;
  const routeState = location.state as { renderId?: string; recovery?: 'playback_interrupted' } | null;
  const requestedRenderId = routeState?.renderId;
  const recoveringFromPlayback = routeState?.recovery === 'playback_interrupted';

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);

  useEffect(() => () => handoffControllerRef.current?.abort(), []);

  useEffect(() => { void fetchStories(); }, [fetchStories]);

  useEffect(() => {
    if (!pair || context.status === 'loading' || context.status === 'error' || context.status === 'needs_selection' || context.status === 'no_ready_voice' || context.status === 'no_listener' || context.status === 'missing_bookends') return;
    const controller = new AbortController();
    const requestPairKey = `${pair.householdId}:${pair.listenerId}:${pair.voiceId}`;
    const requestId = ++loadRequestRef.current;
    setStatus('loading');
    setError(null);
    setOfflineBlocked(false);
    setLoadedPairKey(null);
    fetchLibrary(pair, controller.signal)
      .then((renders) => {
        if (requestId !== loadRequestRef.current || controller.signal.aborted || requestPairKey !== pairKeyRef.current) return;
        const exact = latestApprovedPerStory(renders.filter((render) => render.status === 'parent_approved' && render.household_id === pair.householdId && render.listener_id === pair.listenerId && render.voice_id === pair.voiceId));
        const currentCacheEpoch = getKnownCacheEpoch(pair.householdId);
        const cacheReady = exact.filter((render) => isOfflineCacheCurrent(userId, render.render_id, pair.householdId, pair.listenerId, pair.voiceId, currentCacheEpoch));
        const available = online ? exact : cacheReady;
        setApproved(available);
        const requested = requestedRenderId && available.some((render) => render.render_id === requestedRenderId)
          ? requestedRenderId
          : available[0]?.render_id;
        setSelectedIds(requested ? [requested] : []);
        setLoadedPairKey(requestPairKey);
        setStatus(available.length ? 'success' : 'empty');
      })
      .catch((requestError) => {
        if (controller.signal.aborted || requestId !== loadRequestRef.current || requestPairKey !== pairKeyRef.current) return;
        const safeError = toApiError(requestError);
        if ((safeError.code === 'offline' || (state.offlineSession && safeError.status === null))) {
          const cached = offlineCacheEntriesForPair(userId, pair, getKnownCacheEpoch(pair.householdId));
          const cachedRows = cached.map((entry) => ({
            render_id: entry.renderId,
            story_id: entry.storyId || '',
            voice_id: entry.voiceId,
            household_id: entry.householdId,
            child_slug: null,
            listener_id: entry.listenerId,
            listener_name: null,
            status: 'parent_approved' as const,
            created_at: entry.confirmedAt,
          }));
          setApproved(cachedRows);
          setSelectedIds(cachedRows.length ? [cachedRows[0].render_id] : []);
          setLoadedPairKey(requestPairKey);
          setStatus(cachedRows.length ? 'success' : 'empty');
          return;
        }
        setError(safeError); setLoadedPairKey(requestPairKey); setStatus('error');
      });
    return () => {
      controller.abort();
      loadRequestRef.current += 1;
    };
  }, [context.status, online, pair, requestedRenderId, retryNonce, state.offlineSession, userId]);

  useEffect(() => {
    setApproved([]);
    setSelectedIds([]);
    setLoadedPairKey(null);
    setQueueMode(false);
    setShowStoryPicker(false);
    setOfflineBlocked(false);
    handoffControllerRef.current?.abort();
  }, [pair?.householdId, pair?.listenerId, pair?.voiceId]);

  const storyTitle = (storyId: string) => state.stories.find((story) => story.story_id === storyId)?.title || storyId;
  const selected = useMemo(() => selectedIds.map((id) => approved.find((render) => render.render_id === id)).filter((render): render is RenderListItem => Boolean(render)), [approved, selectedIds]);
  const offlineReadyIds = useMemo(() => new Set(approved.filter((render) => isOfflineCacheCurrent(state.user?.id || '', render.render_id, pair?.householdId || '', pair?.listenerId || '', pair?.voiceId || '', pair ? getKnownCacheEpoch(pair.householdId) : null)).map((render) => render.render_id)), [approved, pair, state.user?.id]);
  const offlineStartAllowed = online || (selected.length > 0 && selected.every((render) => offlineReadyIds.has(render.render_id)));
  const selectedDuration = useMemo(() => queuedDurationMinutes(selectedIds, approved), [approved, selectedIds]);
  const selectedOfflineReady = selected.length > 0 && selected.every((render) => offlineReadyIds.has(render.render_id));

  const toggleStory = (renderId: string) => {
    if (!queueMode) {
      setSelectedIds([renderId]);
      setShowStoryPicker(false);
      return;
    }
    setSelectedIds((current) => current.includes(renderId) ? current.filter((id) => id !== renderId) : current.length < 6 ? [...current, renderId] : current);
  };

  const moveSelected = (renderId: string, direction: -1 | 1) => {
    setSelectedIds((current) => {
      const index = current.indexOf(renderId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const removeSelected = (renderId: string) => {
    setSelectedIds((current) => current.filter((id) => id !== renderId));
  };

  const start = async () => {
    if (!pair || !selected.length || !state.user?.id) return;
    const requestPairKey = `${pair.householdId}:${pair.listenerId}:${pair.voiceId}`;
    setStarting(true);
    handoffControllerRef.current?.abort();
    const controller = new AbortController();
    handoffControllerRef.current = controller;
    try {
      // Online handoff is a fresh exact-pair preflight immediately before the
      // session is written. The child route never receives an optimistic list.
      let handoffRows = approved;
      if (online) {
        const fresh = await fetchLibrary(pair, controller.signal);
        handoffRows = latestApprovedPerStory(fresh.filter((render) => (
          render.status === 'parent_approved'
          && render.household_id === pair.householdId
          && render.listener_id === pair.listenerId
          && render.voice_id === pair.voiceId
        )));
      }
      if (controller.signal.aborted || requestPairKey !== pairKeyRef.current) return;
      const handoffIds = new Set(handoffRows.map((render) => render.render_id));
      const selectedForHandoff = selectedIds.filter((id) => handoffIds.has(id) && (online || offlineReadyIds.has(id)));
      if (!selectedForHandoff.length) {
        if (!online) {
          setOfflineBlocked(true);
          return;
        }
        setApproved(handoffRows);
        setSelectedIds(handoffRows.length ? [handoffRows[0].render_id] : []);
        setStatus(handoffRows.length ? 'success' : 'empty');
        return;
      }
      const offlineReadyRenderIds = handoffRows
        .filter((render) => isOfflineCacheCurrent(userId, render.render_id, pair.householdId, pair.listenerId, pair.voiceId, getKnownCacheEpoch(pair.householdId)))
        .map((render) => render.render_id);
      const session = buildStoryModeSession(pair, selectedForHandoff[0], handoffRows.map((render) => render.render_id), {
        userId: state.user.id,
        queueRenderIds: queueMode && selectedForHandoff.length > 1 ? selectedForHandoff : [],
        offlineReadyRenderIds,
        validationMode: online ? 'online' : 'offline_cache',
      });
      writeStoryModeSession(state.user.id, session);
      navigate('/story-time');
    } catch (requestError) {
      if (!controller.signal.aborted && requestPairKey === pairKeyRef.current) {
        setError(toApiError(requestError));
        setLoadedPairKey(currentPairKey);
        setStatus('error');
      }
    } finally {
      if (handoffControllerRef.current === controller) {
        handoffControllerRef.current = null;
        setStarting(false);
      }
    }
  };

  if (context.status === 'needs_selection') {
    return <div className="storytime-setup"><div className="storytime-setup__container"><EmptyState headingLevel={1} title="Who’s listening tonight?" message="Choose the listener and voice to see their bedtime stories." action={{ label: 'Choose listener and voice', onClick: () => context.openPairDialog() }} /></div></div>;
  }
  if (context.status === 'no_ready_voice' || context.status === 'no_listener') {
    return <div className="storytime-setup"><div className="storytime-setup__container"><EmptyState headingLevel={1} title={context.status === 'no_ready_voice' ? 'Add a family voice' : 'Add a listener'} message={context.status === 'no_ready_voice' ? 'A parent can add a Family voice before starting Story Mode.' : 'Add a listener in Family settings before starting Story Mode.'} action={context.status === 'no_ready_voice' ? { label: 'Family voices', to: '/voice' } : { label: 'Account & family', to: '/account' }} /></div></div>;
  }
  if (context.status === 'missing_bookends' && pair) {
    const listenerName = context.listeners.find((listener) => listener.listener_id === pair.listenerId)?.display_name || 'this listener';
    return <div className="storytime-setup"><div className="storytime-setup__container"><EmptyState headingLevel={1} title={`Add ${listenerName}’s hello and goodnight`} message="A parent can record the three personal moments before handing the device to a child." action={{ label: 'Record personal messages', to: `/voice/${pair.voiceId}/personalize/${pair.listenerId}` }} /></div></div>;
  }

  const pairLoaded = loadedPairKey === currentPairKey;
  return (
    <div className="storytime-setup">
      <div className="storytime-setup__container">
        <header className="storytime-setup__header"><h1>Choose a bedtime story</h1><div data-testid="storytime-pair-confirmation"><BedtimePairChip /></div></header>
        {recoveringFromPlayback && <StateMessage kind="error" title="Story Mode paused" message="The story stopped before it finished. Check the selected story below, then try Story Mode again or choose another approved story." testId="storytime-recovery" />}
        {!pairLoaded && context.status !== 'error' && <StateMessage kind="loading" shape="form" title="Checking approved stories…" testId="storytime-setup-loading" />}
        {pairLoaded && status === 'error' && <StateMessage kind="error" error={error} retry={{ label: 'Try again', onClick: () => setRetryNonce((value) => value + 1) }} testId="storytime-setup-error" />}
        {pairLoaded && status === 'empty' && <EmptyState headingLevel={2} title={online ? 'Your bedtime shelf starts here' : 'These stories aren’t saved on this device'} message={online ? 'Listen to a story in Tonight and approve it before handing over the device.' : 'Connect once to save an approved story for bedtime on this device.'} action={{ label: online ? 'Open Tonight' : 'Try again', onClick: () => { if (online) navigate('/renders'); else setRetryNonce((value) => value + 1); } }} />}
        {pairLoaded && status === 'success' && !offlineStartAllowed && <StateMessage kind="error" title="This story isn’t saved on this device" message="Connect once to save the selected approved story before handing over the device." retry={{ label: 'Try again', onClick: () => setRetryNonce((value) => value + 1) }} />}
        {offlineBlocked && <StateMessage kind="error" title="This story isn’t saved on this device" message="Connect once to save an approved story before handing over the device." retry={{ label: 'Try again', onClick: () => setRetryNonce((value) => value + 1) }} />}
        {pairLoaded && status === 'success' && (
          <>
            <div className="storytime-setup__choice-summary" id="storytime-selection-summary" role="status" aria-live="polite">
              <span><strong>{selected.length === 1 ? 'One story selected' : `${selected.length} stories selected`}</strong><small>{selectedDuration > 0 ? `About ${Math.max(1, Math.ceil(selectedDuration))} min` : 'Duration unavailable'}</small></span>
              <span className="storytime-setup__readiness">{selectedOfflineReady ? 'Saved for bedtime on this device' : online ? 'Internet needed to start' : 'Not saved on this device'}</span>
            </div>
            {selected.length > 0 && <ol className="storytime-setup__queue" aria-label="Selected stories">{selected.map((render, index) => <li key={render.render_id}><span className="storytime-setup__selected-story"><span className="storytime-setup__art"><StoryArtwork src={state.stories.find((story) => story.story_id === render.story_id)?.thumbnail_url} alt="" fallbackClassName="storytime-setup__art-fallback" /></span><span><strong>{queueMode ? `${index + 1}. ` : ''}{storyTitle(render.story_id)}</strong><small>{render.output_seconds ? `${Math.max(1, Math.round(render.output_seconds / 60))} min` : 'Approved for bedtime'}{offlineReadyIds.has(render.render_id) ? ' · Saved on this device' : ''}</small></span></span>{queueMode && <span className="storytime-setup__queue-actions"><button type="button" className="storytime-setup__order" onClick={() => moveSelected(render.render_id, -1)} disabled={index === 0} aria-label={`Move ${storyTitle(render.story_id)} earlier`}>↑</button><button type="button" className="storytime-setup__order" onClick={() => moveSelected(render.render_id, 1)} disabled={index === selected.length - 1} aria-label={`Move ${storyTitle(render.story_id)} later`}>↓</button><PillButton size="compact" onClick={() => removeSelected(render.render_id)} aria-label={`Remove ${storyTitle(render.story_id)} from Story Mode`}>Remove</PillButton></span>}</li>)}</ol>}
            <div className="storytime-setup__actions"><PillButton onClick={() => navigate('/my-stories')}>Cancel</PillButton><PillButton variant="primary" disabled={!selected.length || !offlineStartAllowed} busy={starting} busyLabel="Checking stories…" aria-describedby="storytime-selection-summary" onClick={() => void start()}>Start</PillButton></div>
            <div className="storytime-setup__selection-actions">
              {!queueMode && approved.length > 1 && <PillButton aria-expanded={showStoryPicker} aria-controls="storytime-story-picker" onClick={() => setShowStoryPicker((shown) => !shown)}>Choose a different story</PillButton>}
              {!queueMode && approved.length > 1 && <PillButton onClick={() => { setQueueMode(true); setShowStoryPicker(true); }}>Add another story</PillButton>}
              {queueMode && <PillButton aria-expanded={showStoryPicker} aria-controls="storytime-story-picker" onClick={() => setShowStoryPicker((shown) => !shown)}>{showStoryPicker ? 'Done choosing' : 'Add or remove stories'}</PillButton>}
            </div>
            {showStoryPicker && <section id="storytime-story-picker" className="storytime-setup__picker" aria-labelledby="storytime-picker-title"><h2 id="storytime-picker-title">Choose tonight’s stories</h2><p>{queueMode ? 'Choose up to six stories. Only this parent-built queue will play automatically.' : 'Choosing a story replaces the current selection.'}</p><ul className="storytime-setup__list" aria-label="Approved stories">
              {approved.map((render) => {
                const isSelected = selectedIds.includes(render.render_id);
                return <li key={render.render_id}><button type="button" className={`storytime-setup__story${isSelected ? ' storytime-setup__story--selected' : ''}`} onClick={() => toggleStory(render.render_id)} aria-pressed={isSelected} disabled={queueMode && !isSelected && selectedIds.length >= 6}><span className="storytime-setup__check" aria-hidden="true">{isSelected ? '✓' : ''}</span><span><strong>{storyTitle(render.story_id)}</strong><small>{render.output_seconds ? `${Math.max(1, Math.round(render.output_seconds / 60))} min` : 'Approved for bedtime'}{offlineReadyIds.has(render.render_id) ? ' · Saved on this device' : ''}</small></span></button></li>;
              })}
            </ul></section>}
          </>
        )}
      </div>
    </div>
  );
};

export default StoryTimeSetup;
